import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FileText, ArrowRight, Loader2 } from "lucide-react";
import { getBlogPosts } from "../services/api";
import { PublicNavbar, PublicFooter } from "./PublicLayout";
import "./LandingPage.css";
import "./Blog.css";

function BlogList() {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadPosts();
  }, [page]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const data = await getBlogPosts(page, 9);
      if (data.success) {
        setPosts(data.posts);
        setTotalPages(data.total_pages);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="landing-page">
      <PublicNavbar />

      <section className="sp-hero">
        <div className="sp-hero-pattern" />
        <div className="sp-hero-content">
          <h1>{t("blog.heroTitle")}</h1>
          <p>{t("blog.heroDesc")}</p>
        </div>
      </section>

      {loading ? (
        <div className="blog-loading">
          <Loader2 size={28} className="spinner" />
        </div>
      ) : posts.length === 0 ? (
        <div className="blog-empty">
          <div className="blog-empty-icon">
            <FileText size={24} />
          </div>
          <h3>{t("blog.emptyTitle")}</h3>
          <p>{t("blog.emptyDesc")}</p>
        </div>
      ) : (
        <>
          <div className="blog-grid">
            {posts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="blog-card"
              >
                {post.cover_image_url ? (
                  <img
                    src={post.cover_image_url}
                    alt={post.title}
                    className="blog-card-cover"
                  />
                ) : (
                  <div className="blog-card-placeholder">
                    <FileText size={36} />
                  </div>
                )}
                <div className="blog-card-body">
                  <div className="blog-card-date">
                    {formatDate(post.created_at)}
                  </div>
                  <h2 className="blog-card-title">{post.title}</h2>
                  {post.excerpt && (
                    <p className="blog-card-excerpt">{post.excerpt}</p>
                  )}
                  <span className="blog-card-read">
                    {t("blog.readMore")} <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="blog-pagination">
              <button
                className="blog-page-btn"
                onClick={() => setPage((p) => p - 1)}
                disabled={page <= 1}
              >
                {t("blog.prev")}
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  className={`blog-page-btn ${p === page ? "active" : ""}`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              ))}
              <button
                className="blog-page-btn"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages}
              >
                {t("blog.next")}
              </button>
            </div>
          )}
        </>
      )}

      <PublicFooter />
    </div>
  );
}

export default BlogList;
